'use client';

import React from 'react';

import Text from '@/components/common/Text';
import { useTeamData } from '@/hooks/swr/teams/useTeamData';
import { TeamModel } from '@/types/teams';
import { getFilePath, formatNumber } from '@/utils';

interface TeamProfileWithJoinButtonProps {
  propsTeam: TeamModel;
}

/**
 * チームプロフィール(参加ボタンつき)
 * @param param0
 * @returns
 */
const TeamProfileWithJoinButton: React.FC<TeamProfileWithJoinButtonProps> = ({ propsTeam }) => {
  // SWRを使用して初期データを取得
  // NOTE: メンバー数とログ数を取得。propsTeamと若干だぶるが、パフォーマンスに影響しないコンポーネントかつ、SWRを使用する
  // ことで現状の作りを許容した。
  const { data: teamData, isLoading: isTeamDataLoading } = useTeamData(propsTeam.uuid);

  return (
    <>
      <ul className="flex flex-col justify-center items-center">
        {propsTeam.team_emblem ? (
          <li>
            <img
              src={getFilePath(propsTeam.team_emblem.file_path)}
              alt="teamEmblem"
              className="w-36 h-36 object-contain"
            />
          </li>
        ) : (
          <li>
            <div className="text-gray-200" style={{ color: `${propsTeam.color_a}` }}>
              <svg
                className="w-36 h-36"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 24 24">
                <path d="M12.356 3.066a1 1 0 0 0-.712 0l-7 2.666A1 1 0 0 0 4 6.68a17.695 17.695 0 0 0 2.022 7.98 17.405 17.405 0 0 0 5.403 6.158 1 1 0 0 0 1.15 0 17.406 17.406 0 0 0 5.402-6.157A17.694 17.694 0 0 0 20 6.68a1 1 0 0 0-.644-.949l-7-2.666Z" />
              </svg>
            </div>
          </li>
        )}
        <li>
          <div
            className={`font-semibold text-gray-600 text-center py-4 mb-4
              ${propsTeam.name.length >= 1 && propsTeam.name.length <= 10 && ' text-2xl'}
              ${propsTeam.name.length >= 11 && propsTeam.name.length <= 15 && ' text-lg'}
              ${propsTeam.name.length >= 16 && ' text-base'}
            `}>
            {propsTeam.name}
          </div>
        </li>
        {propsTeam.description !== '' && (
          <li>
            <div className="text-gray-500 text-sm mb-4">
              <Text>{propsTeam.description}</Text>
            </div>
          </li>
        )}
        <li className="w-full">
          <ul className="relative grid grid-cols-2 gap-4 sm:grid-cols-auto-fill sm:grid-rows-auto md:grid-cols-auto-fill md:minmax-300 mb-2">
            <li>
              <div
                className="min-h-16 bg-white font-footballlog rounded-lg shadow-sm py-2 px-4 bg-no-repeat bg-right"
                style={{ backgroundImage: "url('/images/soccerball_gray.svg')", backgroundSize: '85%', width: '100%' }}>
                <div className="text-lg font-footballlog font-semibold text-gray-500 italic">Members</div>
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-gray-400 italic">
                    OBOG: {isTeamDataLoading ? '-' : formatNumber(teamData?.data?.team.obog_members)}
                  </span>
                  <span className="text-4xl text-gray-500 italic font-semibold">
                    {isTeamDataLoading ? '-' : formatNumber(teamData?.data?.team.active_members)}
                  </span>
                </div>
              </div>
            </li>
            <li>
              <div
                className="min-h-16 bg-white font-footballlog rounded-lg shadow-sm py-2 px-4 bg-no-repeat bg-right"
                style={{ backgroundImage: "url('/images/soccerball_gray.svg')", backgroundSize: '85%', width: '100%' }}>
                <div className="text-lg font-footballlog font-semibold text-gray-500 italic">TeamNotes</div>
                <div className="text-right">
                  <span className="text-4xl text-gray-500 italic font-semibold">
                    {isTeamDataLoading ? '-' : formatNumber(teamData?.data?.team.total_team_logs)}
                  </span>
                </div>
              </div>
            </li>
          </ul>
        </li>
        <li className="flex justify-center">
          {propsTeam.url_home && (
            <div className="w-full flex justify-center items-center">
              <a
                href={propsTeam.url_home}
                target="_blank"
                className="w-16 h-16 flex justify-center items-center hover:bg-gray-50 hover:rounded-full text-gray-700 cursor-pointer">
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    fillRule="evenodd"
                    d="M11.293 3.293a1 1 0 0 1 1.414 0l6 6 2 2a1 1 0 0 1-1.414 1.414L19 12.414V19a2 2 0 0 1-2 2h-3a1 1 0 0 1-1-1v-3h-2v3a1 1 0 0 1-1 1H7a2 2 0 0 1-2-2v-6.586l-.293.293a1 1 0 0 1-1.414-1.414l2-2 6-6Z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
          )}
          {propsTeam.url_x && (
            <div className="w-full flex justify-center items-center">
              <a
                href={propsTeam.url_x}
                target="_blank"
                className="w-16 h-16 flex justify-center items-center hover:bg-gray-50 hover:rounded-full text-gray-700 cursor-pointer">
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 24 24">
                  <path d="M13.795 10.533 20.68 2h-3.073l-5.255 6.517L7.69 2H1l7.806 10.91L1.47 22h3.074l5.705-7.07L15.31 22H22l-8.205-11.467Zm-2.38 2.95L9.97 11.464 4.36 3.627h2.31l4.528 6.317 1.443 2.02 6.018 8.409h-2.31l-4.934-6.89Z" />
                </svg>
              </a>
            </div>
          )}
          {propsTeam.url_facebook && (
            <div className="w-full flex justify-center items-center">
              <a
                href={propsTeam.url_facebook}
                target="_blank"
                className="w-16 h-16 flex justify-center items-center hover:bg-gray-50 hover:rounded-full text-gray-700 cursor-pointer">
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    fillRule="evenodd"
                    d="M13.135 6H15V3h-1.865a4.147 4.147 0 0 0-4.142 4.142V9H7v3h2v9.938h3V12h2.021l.592-3H12V6.591A.6.6 0 0 1 12.592 6h.543Z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
          )}
          {propsTeam.url_instagram && (
            <div className="w-full flex justify-center items-center">
              <a
                href={propsTeam.url_instagram}
                target="_blank"
                className="w-16 h-16 flex justify-center items-center hover:bg-gray-50 hover:rounded-full text-gray-700 cursor-pointer">
                <svg
                  className="w-6 h-6"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    fillRule="evenodd"
                    d="M3 8a5 5 0 0 1 5-5h8a5 5 0 0 1 5 5v8a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5V8Zm5-3a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3V8a3 3 0 0 0-3-3H8Zm7.597 2.214a1 1 0 0 1 1-1h.01a1 1 0 1 1 0 2h-.01a1 1 0 0 1-1-1ZM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm-5 3a5 5 0 1 1 10 0 5 5 0 0 1-10 0Z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
          )}
        </li>
      </ul>
    </>
  );
};

export default TeamProfileWithJoinButton;
